import { css } from '@emotion/react';
import {
  BatchCreateBalancesPaymentRequestsRequest_ExistingBalanceRequestOption,
  BatchCreateBalancesPaymentRequestsRequest_NewBalanceCreateRequestOption,
  BatchCreateBalancesPaymentRequestsRequest_UpdatedBalanceRequestOption,
} from '@weave/schema-gen-ts/dist/schemas/payments/messages/commands/commands.pb';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { BannerNotification, FieldProps, Modal, Text } from '@frontend/design-system';
import { fields } from '../types';
import { BalancesAccordian } from './balances-accordian';

type BalancesFormProps = {
  getFieldProps: (name: keyof fields) => FieldProps<fields, keyof fields>;
  balancesCount: {
    newBalances: number;
    changedBalances: number;
    existingBalances: number;
  };
  onClose: () => void;
  disabled?: boolean;
  onSubmit: (e: React.FormEvent) => void;
};
export const BalancesForm = ({ getFieldProps, balancesCount, onClose, onSubmit, disabled }: BalancesFormProps) => {
  const { t } = useTranslation('payments');

  return (
    <>
      <Modal.Body>
        <Text color='subdued'>
          {t(
            'Below you can see an overview of how the data we pull from your EHR ledger currently connects to outstanding payment requests in Weave.'
          )}
        </Text>

        {disabled && (
          <BannerNotification
            status='warn'
            css={css`
              margin: ${theme.spacing(2, 0)};
            `}
          >
            {t('No changes detected. Please review each section below to continue.')}
          </BannerNotification>
        )}

        <BalancesAccordian
          title={t('New Balances')}
          subtitle={t('Create new payment request')}
          body={t('New balances for patients with no other outstanding payment requests')}
          options={[
            {
              label: t('Create New Payment Request'),
              value:
                BatchCreateBalancesPaymentRequestsRequest_NewBalanceCreateRequestOption.NEW_BALANCE_CREATE_REQUEST_OPTION_CREATE,
            },
            {
              label: t('Ignore'),
              value:
                BatchCreateBalancesPaymentRequestsRequest_NewBalanceCreateRequestOption.NEW_BALANCE_CREATE_REQUEST_OPTION_IGNORE,
            },
          ]}
          fieldProps={getFieldProps('newBalance')}
          count={balancesCount?.newBalances}
        />

        <BalancesAccordian
          title={t('Changed Balances')}
          subtitle={t('Replace with updated balance')}
          body={t(
            'Balances with pre-existing payment requests and have gone either up or down since the last time payment requests were created.'
          )}
          options={[
            {
              label: t('Replace'),
              value:
                BatchCreateBalancesPaymentRequestsRequest_UpdatedBalanceRequestOption.UPDATED_BALANCE_REQUEST_OPTION_REPLACE,
            },
            {
              label: t('Ignore'),
              value:
                BatchCreateBalancesPaymentRequestsRequest_UpdatedBalanceRequestOption.UPDATED_BALANCE_REQUEST_OPTION_IGNORE,
            },
          ]}
          fieldProps={getFieldProps('changedBalance')}
          count={balancesCount?.changedBalances}
        />

        <BalancesAccordian
          title={t('Existing Balances')}
          subtitle={t('Ignore and keep the current payment request')}
          body={t(
            'Balances which have an existing payment request and the balance matches what is in the collections list.'
          )}
          options={[
            {
              label: t('Ignore'),
              value:
                BatchCreateBalancesPaymentRequestsRequest_ExistingBalanceRequestOption.EXISTING_BALANCE_REQUEST_OPTION_IGNORE,
            },
            {
              label: t('Replace'),
              value:
                BatchCreateBalancesPaymentRequestsRequest_ExistingBalanceRequestOption.EXISTING_BALANCE_REQUEST_OPTION_REPLACE,
            },
          ]}
          fieldProps={getFieldProps('existingBalance')}
          count={balancesCount?.existingBalances}
        />
      </Modal.Body>
      <Modal.Footer
        primary={{
          label: t('Create All'),
          onClick: (e) => {
            if (!e) return;
            onSubmit(e);
          },
          disabled,
        }}
        secondary={{
          label: t('Cancel'),
          onClick: onClose,
        }}
      />
    </>
  );
};
